import type { IDishDetailsFormValues } from "@/types";
import type { TSupplement } from "@/types";

type SupplementsValue = IDishDetailsFormValues["supplements"];

const cloneSupplements = (supplements: SupplementsValue) =>
  supplements.map((supplement) => ({
    supplementId: supplement.supplementId,
    options: supplement.options.map((option) => ({
      optionId: option.optionId,
      quantity: option.quantity,
    })),
  }));

export const getSupplementIndex = (
  supplements: SupplementsValue,
  supplementId: number
) =>
  supplements.findIndex(
    (supplement) => supplement.supplementId === supplementId
  );

export const getOptionQuantity = (
  supplements: SupplementsValue,
  supplementId: number,
  optionId: number
) => {
  const supplement = supplements.find(
    (supplement) => supplement.supplementId === supplementId
  );
  if (!supplement) return 0;

  const option = supplement.options.find(
    (option) => option.optionId === optionId
  );
  return option?.quantity ?? 0;
};

export const getSelectedCountInGroup = (
  supplements: SupplementsValue,
  supplementId: number
) => {
  const supplement = supplements.find(
    (supplement) => supplement.supplementId === supplementId
  );
  if (!supplement) return 0;

  return supplement.options.reduce(
    (sum, option) => sum + (option.quantity ?? 0),
    0
  );
};

export const clearSupplementGroup = (
  supplements: SupplementsValue,
  supplementId: number
): SupplementsValue => {
  return supplements.filter(
    (supplement) => supplement.supplementId !== supplementId
  );
};

export const setOptionQuantityInGroup = (
  supplements: SupplementsValue,
  supplementId: number,
  optionId: number,
  nextQuantity: number
): SupplementsValue => {
  const safeQty = Math.max(0, nextQuantity);
  const next = cloneSupplements(supplements);

  const supplementIndex = getSupplementIndex(next, supplementId);

  if (safeQty === 0) {
    if (supplementIndex === -1) return next;

    const filteredOptions = next[supplementIndex].options.filter(
      (option) => option.optionId !== optionId
    );

    if (!filteredOptions.length) {
      next.splice(supplementIndex, 1);
      return next;
    }

    next[supplementIndex].options = filteredOptions;
    return next;
  }

  if (supplementIndex === -1) {
    next.push({
      supplementId,
      options: [{ optionId, quantity: safeQty }],
    });
    return next;
  }

  const optionIndex = next[supplementIndex].options.findIndex(
    (option) => option.optionId === optionId
  );

  if (optionIndex === -1) {
    next[supplementIndex].options.push({ optionId, quantity: safeQty });
    return next;
  }

  next[supplementIndex].options[optionIndex].quantity = safeQty;
  return next;
};

export const setSingleRequired = (
  supplements: SupplementsValue,
  supplementId: number,
  optionId: number
): SupplementsValue => {
  const cleared = clearSupplementGroup(supplements, supplementId);
  return setOptionQuantityInGroup(cleared, supplementId, optionId, 1);
};

export const toggleSingleOptional = (
  supplements: SupplementsValue,
  supplementId: number,
  optionId: number
): SupplementsValue => {
  const supplement = supplements.find(
    (supplement) => supplement.supplementId === supplementId
  );
  const selectedOptionId =
    supplement?.options.find((option) => (option.quantity ?? 0) > 0)
      ?.optionId ?? null;

  if (selectedOptionId != null && selectedOptionId !== optionId) {
    return supplements;
  }

  const currentQty = getOptionQuantity(supplements, supplementId, optionId);
  return setOptionQuantityInGroup(
    supplements,
    supplementId,
    optionId,
    currentQty > 0 ? 0 : 1
  );
};

export const changeMultiOptionQty = (args: {
  supplements: SupplementsValue;
  supplement: TSupplement;
  optionId: number;
  delta: number;
}): SupplementsValue => {
  const { supplements, supplement, optionId, delta } = args;

  const groupLimit = supplement.numberOfOption ?? 0;
  const maxPerOption = supplement.maxCountOnOrder ?? null;

  const currentQty = getOptionQuantity(supplements, supplement.id, optionId);
  const nextQty = currentQty + delta;

  if (nextQty <= 0) {
    return setOptionQuantityInGroup(supplements, supplement.id, optionId, 0);
  }

  if (maxPerOption != null && nextQty > maxPerOption) return supplements;

  if (groupLimit > 0) {
    const currentGroupCount = getSelectedCountInGroup(
      supplements,
      supplement.id
    );
    const nextGroupCount = currentGroupCount + delta;

    if (nextGroupCount > groupLimit) return supplements;
  }

  return setOptionQuantityInGroup(
    supplements,
    supplement.id,
    optionId,
    nextQty
  );
};

export const getSelectedOptionsCountInGroup = (
  supplements: SupplementsValue,
  supplementId: number
) => {
  const supplement = supplements.find(
    (supplemen) => supplemen.supplementId === supplementId
  );
  if (!supplement) return 0;

  return supplement.options.filter((option) => (option.quantity ?? 0) > 0)
    .length;
};

export const findFirstInvalidRequiredGroupId = (
  supplementsConfig: TSupplement[],
  selected: SupplementsValue
): number | null => {
  const sorted = [...(supplementsConfig ?? [])].sort((a, b) =>
    (a.groupName ?? "").localeCompare(b.groupName ?? "")
  );

  for (const group of sorted) {
    if (!group.isRequireOnOrder) continue;

    const selectedOptionsCount = getSelectedOptionsCountInGroup(
      selected,
      group.id
    );
    if (selectedOptionsCount === 0) return group.id;
  }

  return null;
};
